import * as React from 'react'
import { CalendarProps } from 'react-calendar'

import { Box } from '../box'
import { ZIndexContext, getExtendedDialogState } from '../dialog'
import { Popover, PopoverTrigger, usePopoverState } from '../popover'
import { Spinner } from '../spinner'

const DatePickerComponent = React.lazy(() => import('./picker'))

type RangePickerCallback = (value: [Date, Date], ev: React.ChangeEvent<HTMLInputElement>) => void
type PickerCallback = (value: Date, ev: React.ChangeEvent<HTMLInputElement>) => void

interface DatePickerProps extends Omit<CalendarProps, 'onChange'> {
  /**
   * Trigger element for the picker
   */
  children: JSX.Element

  onChange: PickerCallback | RangePickerCallback

  zIndex?: string
}

export function StatelessDatePicker({
  state,
  children,
  zIndex,
  ...props
}: DatePickerProps & { state: ReturnType<typeof usePopoverState>; zIndex?: string }) {
  const { isFullyClosed } = getExtendedDialogState(state)
  return (
    <>
      <PopoverTrigger state={state}>
        {(triggerProps: React.HTMLProps<HTMLElement>) =>
          React.Children.map(children, (child) => {
            if (React.isValidElement(child))
              return React.cloneElement(child, { ...triggerProps, ...(child.props as any) })
          })
        }
      </PopoverTrigger>
      {!isFullyClosed && (
        <ZIndexContext.Provider value={zIndex ?? 'z-dialog'}>
          <Popover state={state} focusOnShow={false} theme="none" aria-label="Date picker">
            <React.Suspense
              fallback={
                <Box rounding="none" className="flex h-[202px] w-[235px] items-center justify-center">
                  <Spinner width={16} />
                </Box>
              }
            >
              <DatePickerComponent
                {...props}
                locale="en-US"
                minDetail="year"
                onChange={(value: any, ev: React.ChangeEvent<HTMLInputElement>) => {
                  state.hide()
                  props.onChange?.(value, ev)
                }}
              />
            </React.Suspense>
          </Popover>
        </ZIndexContext.Provider>
      )}
    </>
  )
}

export function DatePicker(props: DatePickerProps) {
  const popover = usePopoverState()

  return <StatelessDatePicker {...props} state={popover} />
}
